export interface User {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  org_id: string;
  org_name: string | null;
  org_kind?: string | null;
  team_visibility?: string | null;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000/api/v1";

let refreshPromise: Promise<boolean> | null = null;

export function setTokens(tokens: TokenResponse) {
  localStorage.setItem("access_token", tokens.access_token);
  localStorage.setItem("refresh_token", tokens.refresh_token);
}

export function clearTokens() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
}

export function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

export function isAccessTokenExpiringSoon(leewaySeconds = 30): boolean {
  const token = getAccessToken();
  if (!token || typeof window === "undefined") return true;

  try {
    const [, encodedPayload] = token.split(".");
    if (!encodedPayload) return false;

    const base64Payload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, "=");
    const payload = JSON.parse(window.atob(paddedPayload));
    if (typeof payload.exp !== "number") return false;

    return payload.exp <= Math.floor(Date.now() / 1000) + leewaySeconds;
  } catch {
    return false;
  }
}

async function performTokenRefresh(): Promise<boolean> {
  if (typeof window === "undefined") return false;

  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) return false;

    const tokens = await res.json();
    setTokens(tokens);
    return true;
  } catch {
    return false;
  }
}

export function refreshTokens(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}
